/**
 * UserWay Website Accessibility widget (app embed, config/settings_data.json)
 * — US-market restriction, hover-scale removal and hover-colour swap.
 *
 * Also carries the site-wide external-links behaviour (TASK-55121) as its
 * own block at the end of the file, kept here rather than in a separate
 * asset to avoid another global script request.
 *
 * All three read data-market-country off <body> — rendered by
 * layout/theme.liquid from localization.country.iso_code, the same
 * attribute assets/analytics-country.js reads for the same reason: it is
 * the one source of truth for the shopper's resolved market already
 * present on the page.
 *
 * ── Market restriction ──────────────────────────────────────────────────
 * UserWay has no country/market targeting of its own — only manual
 * position/appearance settings and an on/off icon toggle on the instance
 * `userway:init_completed` carries. Confirmed against UserWay's own docs
 * (userway.org/docs) before writing this: that toggle is the full extent of
 * the client-side API, so gating by market has to happen here, in the
 * theme. Hooking the init event, not polling for the instance, means the
 * icon is hidden before its first paint on any market but the US, rather
 * than flashing on and then off.
 *
 * ── Hover scale / colour (US market only) ───────────────────────────────
 * UserWay writes the icon's background as an inline
 * `style="background: rgb(...) !important"` from its own widget script
 * (the "widget_color" tuning value, confirmed against the live rendered
 * markup, TASK-54329). No stylesheet rule can beat that — an inline
 * `!important` declaration always outranks a stylesheet's `!important` for
 * the same property, whatever the selector's specificity — so the only way
 * to change what paints on hover is to overwrite that same inline
 * declaration at the same priority, which is what bind() does below: on
 * mouseenter/mouseleave for genuine hover, corrected back to the dashboard
 * default by a short poll whenever the pointer isn't over the icon (see
 * bind()'s own comment for why it polls rather than watches for UserWay's
 * menu opening/closing).
 *
 * The hover scale is also removed: src/css/theme.css carries an id-selector
 * `#userwayAccessibilityIcon:hover { transform: none !important }`, which
 * wins IF UserWay's own hover-scale rule is no more specific and loads no
 * later in the cascade — true today, but not something this theme controls
 * going forward (UserWay ships its widget CSS from its own CDN, versioned
 * independently of this repo). Setting the same inline transform here makes
 * the removal certain rather than contingent on that race — and the
 * standalone `scale` property is reset here too, not just in that CSS rule,
 * since `transform` and `scale` compose independently and the compiled CSS's
 * own `scale: 1 !important` companion gets merged away by the minifier (see
 * bindHover()'s own comment).
 */
(function () {
  'use strict';

  var CURRENT_MARKET = ((document.body && document.body.getAttribute('data-market-country')) || '').toUpperCase();
  var HOVER_BG = 'var(--color-brand-secondary-blue)';

  function hideIcon(event) {
    var instance = event.detail && event.detail.userWayInstance;
    if (instance && typeof instance.iconVisibilityOff === 'function') {
      instance.iconVisibilityOff();
    }
  }

  function bindHover(icon) {
    if (icon.__uwHoverBound) return;
    icon.__uwHoverBound = true;
    icon.style.setProperty('transform', 'none', 'important');
    icon.style.setProperty('scale', '1', 'important');
    var restBg = icon.style.background;
    var hovering = false;

    function setNormal() {
      if (!restBg) restBg = icon.style.background;
      if (restBg) icon.style.setProperty('background', restBg, 'important');
    }

    icon.addEventListener('mouseenter', function () {
      hovering = true;
      if (!restBg) restBg = icon.style.background;
      icon.style.setProperty('background', HOVER_BG, 'important');
    });
    icon.addEventListener('mouseleave', function () {
      hovering = false;
      setNormal();
    });

    /* Clicking the icon is what opens (or closes) UserWay's menu, and the
       pointer usually doesn't move off the icon when that happens — so
       mouseleave alone won't fire and the poll below skips correction while
       still "hovering". Resetting directly on click ties the colour to the
       actual state-changing action instead of waiting on pointer movement or
       the 250ms poll. */
    icon.addEventListener('click', function () {
      hovering = false;
      setNormal();
    });

    setInterval(function () {
      if (hovering || !restBg) return;
      if (icon.style.background !== restBg) {
        icon.style.setProperty('background', restBg, 'important');
      }
    }, 250);
  }

  document.addEventListener('userway:init_completed', function (event) {
    if (CURRENT_MARKET !== 'US') {
      hideIcon(event);
      return;
    }
    var icon = document.getElementById('userwayAccessibilityIcon');
    if (icon) bindHover(icon);
  });
})();

/*
 * ── External links ──────────────────────────────────────────────────────
 * Opens every external link in a new tab, site-wide (TASK-55121).
 *
 * Links are decorated up front, not on click, so the new-tab behaviour is
 * visible before activation: in the DOM, to screen readers, and in the
 * browser's status bar and context menu (WCAG G201 / F22). Each decorated
 * link gets target="_blank", "noopener" added to its existing rel, and a
 * visually hidden "(opens in a new tab)" label read from
 * body[data-new-tab-label] (locales: general.accessibility.new_tab).
 *
 * A MutationObserver covers markup rendered after load (Tagalys grids,
 * drawers, app widgets).
 *
 * Left alone:
 * - links that already set target (a deliberate "_self" or named window
 *   wins); a target="_blank" link still gets the hidden label
 * - store links: the current host with or without "www.", any Whittard
 *   domain or subdomain (STORE_DOMAINS, e.g. account.whittard.com and the
 *   www.whittard.co.uk links in content), the shop's .myshopify.com domain,
 *   and Shopify customer accounts (shopify.com/<shop id>/account)
 * - rel is only ever added to, never replaced, so nofollow / sponsored / ugc
 *   set in rich text survive. noreferrer is deliberately not added: partners
 *   and Whittard-owned properties keep seeing the store as the referrer.
 */
(function () {
  'use strict';

  var STORE_DOMAINS = ['whittard.com', 'whittard.co.uk'];
  var LABEL = document.body.dataset.newTabLabel || '';
  var decorated = new WeakSet();

  function bareHost(hostname) {
    return hostname.replace(/^www\./, '');
  }

  function isStoreHost(host) {
    return STORE_DOMAINS.some(function (domain) {
      return host === domain || host.endsWith('.' + domain);
    });
  }

  function isStoreLink(link) {
    var host = bareHost(link.hostname);
    return host === bareHost(window.location.hostname)
      || isStoreHost(host)
      || host === (window.Shopify && window.Shopify.shop)
      || (host === 'shopify.com' && /^\/\d+\/account/.test(link.pathname));
  }

  function addLabel(link) {
    if (!LABEL) return;

    var ariaLabel = link.getAttribute('aria-label');
    if (ariaLabel) {
      // aria-label replaces the link's content as its name, so a hidden span would never be read
      link.setAttribute('aria-label', ariaLabel + ' ' + LABEL);
      return;
    }

    var span = document.createElement('span');
    span.className = 'sr-only';
    span.textContent = ' ' + LABEL;
    link.appendChild(span);
  }

  function decorate(link) {
    if (decorated.has(link)) return;
    if (link.protocol !== 'http:' && link.protocol !== 'https:') return;

    if (link.hasAttribute('target')) {
      if (link.target !== '_blank') return;
    } else {
      if (isStoreLink(link)) return;
      link.target = '_blank';
    }

    decorated.add(link);
    link.relList.add('noopener');
    addLabel(link);
  }

  function decorateWithin(root) {
    if (root.matches('a[href]')) decorate(root);
    root.querySelectorAll('a[href]').forEach(decorate);
  }

  decorateWithin(document.body);

  new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType === Node.ELEMENT_NODE) decorateWithin(node);
      });
    });
  }).observe(document.body, { childList: true, subtree: true });
})();
